/**
 * "Personal monthly AI limit" (TMMAPP-0113 §C 9): AI is prepaid, so the plan cap is gone and the
 * limit is the person's own — the plan's maximum, or an amount. It saves `aiMonthlyCapMinor` on the
 * person (0 = none of their own, so the plan's maximum applies, MM-D-134); tmm-api refuses an AI call
 * past the lower of the two (`AI_DISABLED` `PERSON_CAP`).
 */
import { useId, useState, type FormEvent } from 'react';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SubmitButton } from '@/components/SubmitButton';
import { formatMoneyMinor, minorToInputText, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { storedLimit } from './aiLimitPatch';

interface PersonalAiLimitProps {
  currency: string;
  /** The stored limit; `null` or 0 = none. */
  capMinor: number | null;
  /** The plan's maximum (the card's `maxAiSpendMinor`), which applies without a limit of their own. */
  planMaximum: { minor: number; currency: string } | null;
  busy?: boolean;
  /** `null` = no limit. A rejected promise keeps the draft. */
  onSave: (capMinor: number | null) => Promise<void>;
}

export function PersonalAiLimit({
  currency,
  capMinor,
  planMaximum,
  busy = false,
  onSave,
}: PersonalAiLimitProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const labelId = useId();
  const stored = storedLimit(capMinor);
  const savedText = stored !== null ? minorToInputText(stored, currency, locale) : '';
  const savedKey = `${stored ?? ''}:${currency}`;
  const [seenKey, setSeenKey] = useState(savedKey);
  const [limited, setLimited] = useState(stored !== null);
  const [text, setText] = useState(savedText);
  const [touched, setTouched] = useState(false);
  if (seenKey !== savedKey) {
    setSeenKey(savedKey);
    setLimited(stored !== null);
    setText(savedText);
    setTouched(false);
  }
  const parsed = parseMoneyToMinor(text, currency, locale);
  const invalid = limited && (parsed === null || parsed <= 0);
  const next = limited ? parsed : null;
  const dirty = limited !== (stored !== null) || (limited && parsed !== stored);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTouched(true);
    if (invalid || !dirty || busy) return;
    void onSave(next).catch(() => {
      /* the caller says why; the draft stays */
    });
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography id={labelId} variant="subtitle1">
        {t('aiLimit.title')}
      </Typography>
      <RadioGroup
        aria-labelledby={labelId}
        value={limited ? 'limit' : 'none'}
        onChange={(event) => {
          setLimited(event.target.value === 'limit');
          setTouched(false);
        }}
      >
        <FormControlLabel
          value="none"
          control={<Radio />}
          label={
            planMaximum !== null
              ? t('aiLimit.planMaximum', {
                  amount: formatMoneyMinor(planMaximum.minor, planMaximum.currency, locale),
                })
              : t('aiLimit.planMaximumPlain')
          }
        />
        <FormControlLabel value="limit" control={<Radio />} label={t('aiLimit.limited')} />
      </RadioGroup>
      <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', mt: 1 }}>
        {limited && (
          <TextField
            size="small"
            label={t('aiLimit.amount', { currency })}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onBlur={() => setTouched(true)}
            error={touched && invalid}
            helperText={touched && invalid ? t('aiLimit.amountInvalid') : undefined}
            disabled={busy}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            sx={{ minWidth: 200 }}
          />
        )}
        <SubmitButton type="submit" variant="outlined" loading={busy} disabled={!dirty}>
          {t('aiLimit.save')}
        </SubmitButton>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t('aiLimit.help')}
      </Typography>
    </Box>
  );
}
